import React, { forwardRef, Children, cloneElement, useRef, useState, useMemo, useEffect, useCallback, isValidElement } from 'react';
import { useTheme } from 'react-jss';
import { Fade } from '@wds/motion';
import { safeRest, createMemoStyles, getDisplayName, defaultFonts, uniqueId, notEmpty, isBrowser, KEYS, conditionGuard, isElement, handlePortal } from '@wds/utils';
import { List } from '@wds/list';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var _assign = function __assign() {
  _assign = Object.assign || function __assign(t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) {
        if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
      }
    }
    return t;
  };
  return _assign.apply(this, arguments);
};
function __rest(s, e) {
  var t = {};
  for (var p in s) {
    if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];
  }
  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
}
var MenuItem = forwardRef(function (_a, ref) {
  var addOn = _a.addOn,
    _b = _a.addOnPlacement,
    addOnPlacement = _b === void 0 ? 'left' : _b,
    color = _a.color,
    onClick = _a.onClick,
    children = _a.children,
    _c = _a.disabled,
    disabled = _c === void 0 ? false : _c,
    _d = _a.component,
    component = _d === void 0 ? undefined : _d,
    _e = _a.isNavigable,
    isNavigable = _e === void 0 ? false : _e;
  _a.isFocused;
  var _g = _a.marked,
    marked = _g === void 0 ? false : _g,
    _h = _a.upgrade,
    upgrade = _h === void 0 ? false : _h,
    groupindex = _a.groupindex,
    handleMenuClose = _a.handleMenuClose,
    index = _a.index,
    _j = _a.role,
    role = _j === void 0 ? 'menuitem' : _j,
    id = _a.id,
    rest = __rest(_a, ["addOn", "addOnPlacement", "color", "onClick", "children", "disabled", "component", "isNavigable", "isFocused", "marked", "upgrade", "groupindex", "handleMenuClose", "index", "role", "id"]);
  var onHandleClick = function onHandleClick(e) {
    if (onClick) onClick(e);
    if (handleMenuClose) handleMenuClose(e);
    e.stopPropagation();
  };
  return React.createElement(List.Item, _assign({
    addOnLeft: addOnPlacement === 'left' ? addOn : undefined,
    addOnRight: addOnPlacement === 'right' ? addOn : undefined,
    color: color,
    component: component,
    "data-group": groupindex,
    disabled: disabled,
    index: index,
    isNavigable: isNavigable,
    marked: marked,
    menu: true,
    interactive: true,
    onClick: onHandleClick,
    role: role,
    upgrade: upgrade,
    tabIndex: disabled ? -1 : 0,
    ref: ref,
    id: id
  }, safeRest(rest)), children);
});
MenuItem.displayName = 'MenuItem';
var useStyles$4 = createMemoStyles(function (theme) {
  var border = theme.border;
  return {
    wrenchMenuGroup: function wrenchMenuGroup() {
      return {
        borderBottom: border.main
      };
    }
  };
});
var renderListItems = function renderListItems(_a) {
  var children = _a.children,
    groupIndex = _a.groupIndex,
    onKeyDown = _a.onKeyDown,
    handleMenuClose = _a.handleMenuClose;
  var listItems = Children.map(children, function (child) {
    if (!React.isValidElement(child)) return null;
    return cloneElement(child, {
      groupindex: groupIndex,
      handleMenuClose: handleMenuClose,
      onKeyDown: onKeyDown
    });
  });
  return listItems;
};
var MenuGroup = function MenuGroup(_a) {
  var children = _a.children,
    title = _a.title,
    dense = _a.dense,
    groupIndex = _a.groupIndex,
    onKeyDown = _a.onKeyDown,
    handleMenuClose = _a.handleMenuClose,
    rest = __rest(_a, ["children", "title", "dense", "groupIndex", "onKeyDown", "handleMenuClose"]);
  var wrenchMenuGroup = useStyles$4().wrenchMenuGroup;
  return React.createElement("section", {
    className: wrenchMenuGroup
  }, React.createElement(List, _assign({
    noRules: true,
    dense: dense,
    interactive: true
  }, rest), title && React.createElement(List.Item, null, title), renderListItems({
    children: children,
    groupIndex: groupIndex,
    onKeyDown: onKeyDown,
    handleMenuClose: handleMenuClose
  })));
};
MenuGroup.displayName = 'MenuGroup';
var useStyles$3 = createMemoStyles(function (theme) {
  var palette = theme.palette,
    spacing = theme.spacing;
  return {
    wrenchMenuFooter: {
      display: 'flex',
      justifyContent: 'space-between',
      flexShrink: 0,
      padding: [spacing[4], spacing[5]],
      background: palette.background.light,
      borderTop: "1px solid ".concat(palette.border.muted),
      color: palette.text.dark
    },
    wrenchMenuActions: function wrenchMenuActions(_a) {
      var align = _a.align;
      return _assign(_assign(_assign({
        display: 'flex',
        flexGrow: 1,
        flexWrap: 'wrap'
      }, align === 'right' && {
        justifyContent: 'flex-end'
      }), align === 'left' && {
        justifyContent: 'flex-start'
      }), {
        '&:not([class^="wrenchButtonGroup"])': {
          '& > button, & > a': {
            marginLeft: spacing[4]
          }
        }
      });
    }
  };
});
var MenuFooter = function MenuFooter(_a) {
  var _b = _a.align,
    align = _b === void 0 ? 'left' : _b,
    children = _a.children;
  var _c = useStyles$3({
      align: align
    }),
    wrenchMenuActions = _c.wrenchMenuActions,
    wrenchMenuFooter = _c.wrenchMenuFooter;
  return React.createElement("footer", {
    className: wrenchMenuFooter
  }, React.createElement("div", {
    className: wrenchMenuActions
  }, children));
};
var useStyles$2 = createMemoStyles(function (theme) {
  var spacing = theme.spacing;
  return {
    wrenchMenuBody: function wrenchMenuBody(_a) {
      var padding = _a.padding;
      return _assign({
        overflowY: 'auto',
        flexGrow: 1
      }, padding && {
        padding: [spacing[4], spacing[5], spacing[5], spacing[5]]
      });
    }
  };
});
var MenuBody = function MenuBody(_a) {
  var children = _a.children,
    _b = _a.padding,
    padding = _b === void 0 ? false : _b,
    rest = __rest(_a, ["children", "padding"]);
  var wrenchMenuBody = useStyles$2({
    padding: padding
  }).wrenchMenuBody;
  var renderItemContainer = function renderItemContainer() {
    var _a;
    var items = (_a = Children.map(children, function (child) {
      if (getDisplayName(child) === 'MenuItem') {
        return child;
      }
      return null;
    })) !== null && _a !== void 0 ? _a : [];
    // all children are MenuItems, so render as a list
    if (items.length === Children.count(children)) {
      return React.createElement(List, _assign({
        interactive: true
      }, rest), children);
    }
    return children;
  };
  return React.createElement("section", {
    className: wrenchMenuBody
  }, renderItemContainer());
};
MenuBody.displayName = 'MenuBody';
var useStyles$1 = createMemoStyles(function (theme) {
  var palette = theme.palette,
    spacing = theme.spacing,
    _a = theme.type,
    fontWeight = _a.fontWeight,
    fontSize = _a.fontSize;
  return {
    wrenchMenuHeader: function wrenchMenuHeader() {
      return {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        padding: [spacing[5], spacing[5], spacing[4], spacing[5]],
        background: palette.background.light,
        flexShrink: 0,
        flexDirection: 'column'
      };
    },
    wrenchMenuText: {
      fontSize: 20,
      fontWeight: fontWeight.medium,
      color: palette.text.dark,
      marginTop: 0,
      marginBottom: spacing[2]
    },
    wrenchMenuTitle: {
      flexGrow: 1,
      width: '100%'
    },
    wrenchMenuSubText: {
      fontSize: fontSize.bodySm,
      fontWeight: fontWeight.regular,
      color: palette.text.darkMuted,
      margin: [spacing[1], 0, 0]
    },
    wrenchMenuAddOn: {
      flexGrow: 1,
      marginTop: spacing[3],
      width: '100%',
      minWidth: '33%',
      textAlign: 'right',
      // In the case that an addon is the first child (modal title was omitted)
      // we do not want a margin top
      '& :first-child': {
        marginTop: 0
      }
    }
  };
});
var MenuHeader = function MenuHeader(_a) {
  var _b = _a.addOn,
    addOn = _b === void 0 ? undefined : _b,
    _c = _a.header,
    header = _c === void 0 ? undefined : _c,
    _d = _a.id,
    id = _d === void 0 ? undefined : _d,
    _e = _a.subHeader,
    subHeader = _e === void 0 ? undefined : _e;
  if (subHeader && addOn) {
    throw new Error('You cannot have both subHeader and addon');
  } else if (!header && subHeader) {
    throw new Error('You must provide a header if you provide a subHeader');
  }
  var _f = useStyles$1(),
    wrenchMenuAddOn = _f.wrenchMenuAddOn,
    wrenchMenuHeader = _f.wrenchMenuHeader,
    wrenchMenuSubText = _f.wrenchMenuSubText,
    wrenchMenuText = _f.wrenchMenuText,
    wrenchMenuTitle = _f.wrenchMenuTitle;
  return React.createElement("section", {
    className: wrenchMenuHeader
  }, React.createElement("div", {
    className: wrenchMenuTitle
  }, header && React.createElement("h2", {
    className: wrenchMenuText,
    id: id
  }, header), subHeader && React.createElement("p", {
    className: wrenchMenuSubText
  }, subHeader)), addOn && React.createElement("div", {
    className: wrenchMenuAddOn
  }, addOn));
};
var useStyles = createMemoStyles(function (theme) {
  var palette = theme.palette,
    shadow = theme.shadow;
  var menu = {
    boxShadow: shadow.space,
    maxWidth: 300,
    minWidth: 175,
    maxHeight: 325
  };
  return {
    wrenchMenuContainer: function wrenchMenuContainer(_a) {
      var stretched = _a.stretched;
      return _assign(_assign({}, defaultFonts(theme)), {
        position: 'relative',
        display: stretched ? 'block' : 'inline-block',
        outline: 'none'
      });
    },
    wrenchMenu: function wrenchMenu(_a) {
      var menuStyle = _a.menuStyle,
        placement = _a.placement,
        stretched = _a.stretched,
        elevation = _a.elevation;
      return _assign(_assign({
        position: 'absolute',
        zIndex: elevation !== null && elevation !== void 0 ? elevation : theme.elevation.sky,
        display: 'block',
        outline: 'none',
        textAlign: 'left',
        color: palette.text.dark,
        minWidth: menu.minWidth,
        maxWidth: stretched ? 'none' : menu.maxWidth,
        boxShadow: menu.boxShadow,
        whiteSpace: 'noWrap'
      }, placement && {
        'bottom-left': {
          left: 0,
          top: '100%'
        },
        'bottom-right': {
          left: 'auto',
          right: 0,
          top: '100%'
        },
        'top-left': {
          left: 0,
          top: 'auto',
          bottom: '100%'
        },
        'top-right': {
          left: 'auto',
          right: 0,
          top: 'auto',
          bottom: '100%'
        }
      }[placement]), menuStyle);
    },
    wrenchMenuBody: function wrenchMenuBody(_a) {
      var noMaxHeight = _a.noMaxHeight;
      return {
        background: palette.background.light,
        // maxHeight: noMaxHeight ? 'none' : menu.maxHeight,
        maxHeight: noMaxHeight ? '100vh' : '400px',
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column'
      };
    }
  };
});
var FOCUS_STATES;
(function (FOCUS_STATES) {
  FOCUS_STATES["TOP"] = "top";
  FOCUS_STATES["BOTTOM"] = "bottom";
})(FOCUS_STATES || (FOCUS_STATES = {}));
var MENU_PLACEMENTS = ['bottom-right', 'bottom-left', 'top-right', 'top-left'];
var Menu = function Menu(_a) {
  var id = _a.id,
    autoClose = _a.autoClose,
    closeOnBlur = _a.closeOnBlur,
    children = _a.children,
    dense = _a.dense,
    placement = _a.placement,
    menuTrigger = _a.menuTrigger,
    noMaxHeight = _a.noMaxHeight,
    onChange = _a.onChange,
    showProp = _a.show,
    stretched = _a.stretched,
    renderTo = _a.renderTo,
    role = _a.role,
    noRules = _a.noRules,
    elevation = _a.elevation,
    preventDefault = _a.preventDefault,
    stopPropagation = _a.stopPropagation,
    variant = _a.variant,
    rest = __rest(_a, ["id", "autoClose", "closeOnBlur", "children", "dense", "placement", "menuTrigger", "noMaxHeight", "onChange", "show", "stretched", "renderTo", "role", "noRules", "elevation", "preventDefault", "stopPropagation", "variant"]);
  // Refs
  var menuRef = useRef(null);
  var innerMenuRef = useRef(null);
  var itemsRef = useRef([]);
  var autoCloseTimeoutRef = useRef();
  // Menu State Items
  var _b = useState(showProp),
    showState = _b[0],
    setShowState = _b[1];
  var _c = useState({}),
    menuStyle = _c[0],
    setMenuStyle = _c[1];
  var _d = useState(null),
    focusState = _d[0],
    setFocusState = _d[1];
  var _e = useState(-1),
    focusIndex = _e[0],
    setFocusIndex = _e[1];
  // For memoizing handleOnKeyDown
  var keyPropRef = useRef({
    focusIndex: focusIndex,
    showState: showState
  });
  keyPropRef.current = {
    focusIndex: focusIndex,
    showState: showState
  };
  // aria-owns for a11y
  var menuID = useMemo(function () {
    return id !== null && id !== void 0 ? id : uniqueId('wds-menu-');
  }, [id]);
  // Styles
  var theme = useTheme();
  var classes = useStyles({
    menuStyle: menuStyle,
    noMaxHeight: noMaxHeight,
    placement: placement,
    stretched: stretched,
    elevation: elevation
  });
  /**
   * a way to help replicate componentWillReceiveProps
   * and componentDidUpdate
   */
  var prevPropsRef = useRef({});
  useEffect(function () {
    prevPropsRef.current = {
      showProp: showProp,
      showState: showState
    };
    /**
     * Each time the MenuItem List is generated through the menu button trigger
     * this code will create a list of accessible ListItems to traverse through.
     */
    if (showState && innerMenuRef.current) {
      itemsRef.current = [].slice.call(innerMenuRef.current.getElementsByTagName('li')).map(function (item) {
        return item.firstChild;
      }).filter(notEmpty);
    }
    /**
     * When showProp is still set to True, but showState is false,
     * we need sync showState up with showProp
     */
    if (showProp && showState !== showProp) {
      setShowState(showProp);
    }
  }, [showProp, showState, children]);
  var prevProps = prevPropsRef.current;
  // This will auto focus the first or last item
  useEffect(function () {
    var _a;
    if (focusState) {
      var focusInd = focusState === FOCUS_STATES.TOP ? 0 : itemsRef.current.length - 1;
      if (itemsRef.current[focusInd]) {
        (_a = itemsRef.current[focusInd]) === null || _a === void 0 ? void 0 : _a.focus();
      }
      setFocusIndex(focusInd);
    }
  }, [focusState]);
  /**
   * @todo needs Modal
   * Update the placement of the menu when renderTo is passed in.
   */
  var updatePosition = function updatePosition() {
    var _a, _b;
    if (menuRef.current && showState && ((_b = (_a = innerMenuRef === null || innerMenuRef === void 0 ? void 0 : innerMenuRef.current) === null || _a === void 0 ? void 0 : _a.children) === null || _b === void 0 ? void 0 : _b.length)) {
      var rect = menuRef.current.getBoundingClientRect();
      var innerRect = innerMenuRef.current.getBoundingClientRect();
      var plaementMap = {
        'top-left': {
          top: rect.top - innerRect.height,
          left: rect.left
        },
        'top-right': {
          top: rect.top - innerRect.height,
          left: rect.right - innerRect.width
        },
        'bottom-left': {
          top: rect.bottom,
          left: rect.left
        },
        'bottom-right': {
          top: rect.bottom,
          left: rect.right - innerRect.width
        }
      };
      var coordinates = plaementMap[placement !== null && placement !== void 0 ? placement : 'bottom-right'];
      var top_1 = coordinates.top,
        left = coordinates.left;
      var z = (elevation !== null && elevation !== void 0 ? elevation : theme.elevation.space) + 1;
      setMenuStyle({
        top: top_1,
        left: left,
        zIndex: z,
        position: 'fixed',
        right: 'auto',
        bottom: 'auto' // fixes box-shadow not showing
      });
    }
  };

  var handleScroll = function handleScroll() {
    if (showState && renderTo) {
      updatePosition();
    }
  };
  // componentDidMount
  useEffect(function () {
    if (showState && renderTo) updatePosition();
    // ssr doesn't have window object
    if (isBrowser) {
      window.addEventListener('scroll', handleScroll);
      window.addEventListener('resize', handleScroll);
      window.addEventListener('orientationchange', handleScroll);
    }
    // componentWillUnmount
    return function () {
      if (isBrowser) {
        window.removeEventListener('scroll', handleScroll);
        window.removeEventListener('resize', handleScroll);
        window.removeEventListener('orientationchange', handleScroll);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renderTo, showState]);
  // componentDidUpdate
  useEffect(function () {
    if (prevProps) {
      if (!prevProps.showState && showProp && renderTo) {
        updatePosition();
      }
    }
  });
  // componentWillReceiveProps
  useEffect(function () {
    setShowState(showProp);
  }, [showProp]);
  var menuChange = useCallback(function (showMenu) {
    if (!showMenu) {
      setFocusState(null);
      setFocusIndex(-1);
    }
    setShowState(showMenu);
    if (onChange) {
      onChange(showMenu);
    }
  }, [onChange]);
  var handleOnClick = function handleOnClick(e) {
    if (stopPropagation) {
      e.stopPropagation();
    }
    if (preventDefault) {
      e.preventDefault();
    }
    menuChange(!showState);
  };
  var handleKeyScroll = function handleKeyScroll(index) {
    if (itemsRef.current) {
      if (index > -1 && itemsRef.current[index]) {
        var menuItemHeight = itemsRef.current[index].offsetTop + itemsRef.current[index].offsetHeight;
        var menuContainerHeight = innerMenuRef.current.offsetHeight;
        innerMenuRef.current.scrollTop = menuItemHeight <= menuContainerHeight ? 0 : menuItemHeight - menuContainerHeight;
      }
    }
  };
  var handleOnKeyDown = useCallback(function (e) {
    var key = e.key;
    if (keyPropRef.current.showState) {
      var itemsLen = itemsRef.current.length - 1;
      var focusInd = keyPropRef.current.focusIndex === -1 ? 0 : keyPropRef.current.focusIndex;
      var newFocusIndex = 0;
      switch (true) {
        case key === KEYS.TAB && variant === 'list':
        case key === KEYS.ESC:
          menuChange(false);
          if (menuRef.current.firstChild) {
            menuRef.current.firstChild.focus();
          }
          e.preventDefault();
          break;
        /**
         * TODO: Needs to handle space in a better way
         */
        case key === KEYS.ENTER:
          itemsRef.current[focusInd].click();
          break;
        case key === KEYS.UP:
          newFocusIndex = focusInd > 0 ? focusInd - 1 : itemsLen;
          break;
        case key === KEYS.DOWN:
          newFocusIndex = focusInd === itemsLen ? 0 : focusInd + 1;
          break;
      }
      if (key === KEYS.UP || key === KEYS.DOWN) {
        setFocusIndex(newFocusIndex);
        handleKeyScroll(newFocusIndex);
        if (newFocusIndex > -1 && itemsRef.current[newFocusIndex]) {
          itemsRef.current[newFocusIndex].focus();
          e.preventDefault();
        }
      }
    }
  }, [menuChange, variant]);
  var handleOnTriggerKeyDown = function handleOnTriggerKeyDown(e) {
    var key = e.key;
    if (React.isValidElement(menuTrigger) && conditionGuard(menuTrigger.props, Object.prototype.hasOwnProperty.call(menuTrigger.props, 'onKeyDown'))) {
      menuTrigger.props.onKeyDown(e);
    }
    var isNotInput = getDisplayName(menuTrigger) !== 'Input';
    if (isNotInput) {
      switch (key) {
        case KEYS.ENTER:
        case KEYS.SPACE:
        case KEYS.DOWN:
          // TODO: Find a better way to handle this type
          handleOnClick(e);
          setFocusState(FOCUS_STATES.TOP);
          e.preventDefault();
          break;
        case KEYS.UP:
          // TODO: Find a better way to handle this type
          handleOnClick(e);
          setFocusState(FOCUS_STATES.BOTTOM);
          e.preventDefault();
          break;
      }
    }
  };
  var handleAutoCloseBlur = function handleAutoCloseBlur() {
    autoCloseTimeoutRef.current = window.setTimeout(function () {
      var _a;
      // if the menu is already closed, ignore this blur event
      if (!showState) {
        return;
      }
      var renderToComp = null;
      if (renderTo) {
        if (conditionGuard(renderTo, isElement(renderTo))) renderToComp = renderTo;else if (renderTo.current) {
          renderToComp = renderTo.current;
        }
      }
      var isFocusInRenderContainer = renderToComp === null || renderToComp === void 0 ? void 0 : renderToComp.contains(document.activeElement);
      var isFocusInMenuRef = (_a = menuRef.current) === null || _a === void 0 ? void 0 : _a.contains(document.activeElement);
      if (!isFocusInRenderContainer && !isFocusInMenuRef) {
        menuChange(false);
      }
    }, 0);
  };
  // clear the timeout when the component unmounts
  useEffect(function () {
    return function () {
      window.clearTimeout(autoCloseTimeoutRef.current);
    };
  }, []);
  var handleMenuClose = useCallback(function () {
    if (autoClose) {
      menuChange(false);
    }
  }, [autoClose, menuChange]);
  var renderListItems = function renderListItems(ch, groupindex, handleMenu) {
    var listItems = Children.map(ch, function (child) {
      if (!React.isValidElement(child)) return null;
      return cloneElement(child, {
        groupindex: groupindex,
        onKeyDown: handleOnKeyDown,
        handleMenuClose: handleMenu
      });
    });
    return listItems;
  };
  var renderItems = function renderItems(ch) {
    var listItems = Children.map(ch, function (child) {
      if (!React.isValidElement(child)) return null;
      if (getDisplayName(child) === 'MenuBody') {
        return cloneElement(child, {
          dense: dense,
          noRules: noRules
        });
      }
      if (['MenuHeader', 'MenuFooter'].includes(getDisplayName(child))) {
        return child;
      }
      return null;
    });
    return listItems;
  };
  var renderMenuGroup = function renderMenuGroup(el, groupIndex, handleMenu) {
    return cloneElement(el, {
      groupIndex: groupIndex,
      dense: dense,
      onKeyDown: handleOnKeyDown,
      handleMenuClose: handleMenu
    });
  };
  var renderItemContainer = function renderItemContainer() {
    var _a;
    var items = (_a = Children.map(children, function (child, index) {
      if (!isValidElement(child)) {
        return undefined;
      }
      if (getDisplayName(child) !== 'MenuGroup') {
        return undefined;
      }
      return renderMenuGroup(child, index, handleMenuClose);
    })) !== null && _a !== void 0 ? _a : [];
    // children are all Menu.Group
    if (items.length) {
      return items;
    }
    if (variant === 'list') {
      return React.createElement(List, _assign({
        interactive: true,
        dense: dense,
        noRules: noRules
      }, rest), renderListItems(children, 0, handleMenuClose));
    }
    return renderItems(children);
  };
  var renderMenu = function renderMenu() {
    var menuComponent = React.createElement("div", {
      className: classes.wrenchMenu
    }, React.createElement(Fade, {
      in: showState,
      speed: "fast"
    }, React.createElement("div", null, React.createElement("div", {
      className: classes.wrenchMenuBody,
      role: role,
      id: menuID,
      ref: innerMenuRef
    }, renderItemContainer()))));
    return handlePortal(menuComponent, renderTo);
  };
  var renderTrigger = function renderTrigger() {
    var componentName = getDisplayName(menuTrigger);
    var isButton = componentName === 'Button';
    var isInput = componentName === 'Input';
    return cloneElement(menuTrigger, _assign(_assign(_assign({
      'aria-haspopup': role
    }, showState && {
      'aria-owns': menuID
    }), isButton && {
      'aria-pressed': (showState !== null && showState !== void 0 ? showState : false) || showProp
    }), {
      onFocus: isInput ? handleOnClick : undefined,
      onClick: !isInput ? handleOnClick : undefined,
      onKeyDown: handleOnTriggerKeyDown,
      tabIndex: 0
    }));
  };
  return React.createElement("span", _assign({
    className: classes.wrenchMenuContainer,
    onBlur: closeOnBlur ? handleAutoCloseBlur : undefined,
    ref: menuRef,
    tabIndex: -1
  }, safeRest(rest)), renderTrigger(), renderMenu());
};
Menu.defaultProps = {
  autoClose: true,
  closeOnBlur: true,
  dense: false,
  placement: undefined,
  noMaxHeight: false,
  onChange: undefined,
  stretched: false,
  show: false,
  renderTo: undefined,
  id: undefined,
  role: 'menu',
  noRules: false,
  elevation: undefined,
  preventDefault: false,
  stopPropagation: false,
  variant: 'list'
};
Menu.Item = MenuItem;
Menu.Group = MenuGroup;
Menu.Header = MenuHeader;
Menu.Body = MenuBody;
Menu.Footer = MenuFooter;
export { FOCUS_STATES, MENU_PLACEMENTS, Menu };